import type { ReceiptCategory, TravelRateUnit } from "@/api-v3";
import { isFailedPayoutStatus } from "@/features/payments/helpers/payout-status";

export type PaymentKind = "mileage" | "receipt" | "micropayment" | "stipend";

export interface PaymentAccountSummary {
  type: string;
  label: string;
  identifier?: string;
}

export interface VisitSummary {
  name: string;
  date: string;
  referenceId?: string;
  visitType?: string;
}

export interface ReceiptItemSummary {
  description: string;
  amount: string;
  category: ReceiptCategory;
}

export interface MileageDetails {
  kind: "mileage";
  distance: string;
  unit: TravelRateUnit;
  ratePerUnit: string;
  total: string;
}

export interface ReceiptDetails {
  kind: "receipt";
  imageUrl: string;
  merchant?: string;
  receiptDate?: string;
  tax?: string;
  discount?: string;
  additionalFees?: string;
  items: ReceiptItemSummary[];
}

export interface MicropaymentDetails {
  kind: "micropayment";
  configurationLabel?: string;
}

export interface StipendDetails {
  kind: "stipend";
}

export type PaymentDetailsViewDetails =
  | MileageDetails
  | ReceiptDetails
  | MicropaymentDetails
  | StipendDetails;

export interface PaymentDetailsView {
  paymentKind: PaymentKind;
  id: string;
  amount: string;
  /** The Approved Amount, set only when it differs from the claimed `amount`. */
  adjustedAmount?: string;
  currency: string;
  status: string;
  createdAt?: string;
  externalNotes?: string;
  payoutStatus?: string;
  payoutFailureReason?: string;
  /** Date the payment was approved/accepted or rejected, when actioned. */
  decisionDate?: string;
  paymentAccount?: PaymentAccountSummary;
  visit?: VisitSummary;
  /**
   * A User raised this claim on the participant's behalf. The claim shows a
   * study team tag and the participant cannot cancel it.
   */
  raisedByStudyTeam: boolean;
  details: PaymentDetailsViewDetails;
}

export const isPendingStatus = (status: string | null | undefined): boolean =>
  (status ?? "").toLowerCase() === "pending";

export { isFailedPayoutStatus };

/** Whether the payment or its payout has entered a failed state. */
export const isFailedView = (view: PaymentDetailsView): boolean =>
  view.status.toLowerCase() === "failed" ||
  isFailedPayoutStatus(view.payoutStatus);

const CANCELABLE_KINDS: ReadonlySet<PaymentKind> = new Set([
  "mileage",
  "receipt",
]);

export const isCancelablePaymentKind = (
  kind: PaymentKind
): kind is "mileage" | "receipt" => CANCELABLE_KINDS.has(kind);

const ACCEPTABLE_KINDS: ReadonlySet<PaymentKind> = new Set([
  "micropayment",
  "stipend",
]);

export const isAcceptablePaymentKind = (
  kind: PaymentKind
): kind is "micropayment" | "stipend" => ACCEPTABLE_KINDS.has(kind);
